import { useEffect, useState, type ReactNode } from 'react';
import { msUntilExpiry, watchSessionExpiry } from '../services/sessionExpiry';
import { Link } from 'react-router-dom';
import ManagedSignIn from './ManagedSignIn';
import { takePendingStep, type PendingStep } from '../services/managedSignIn';
import {
  IdentityContext,
  MFAAssuranceRequired,
  getAuthConfig,
  getDevUser,
  getMe,
  loginErrorMessage,
  setDevUser,
  setSession,
  signInWithGoogle,
  signInWithMicrosoft,
  signOut,
  useIdentity,
  type AuthConfig,
  type Me,
} from '../services/identity';

/**
 * Resolves who the client acts as. Without a valid RadioOS session (and outside DEV_MODE) the
 * administrative application is not rendered at all: only the sign-in screen.
 */
export function IdentityProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [state, setState] = useState<'loading' | 'signed-in' | 'signed-out' | 'error' | 'mfa'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [sessionEndsAt, setSessionEndsAt] = useState<number | null>(null);

  useEffect(() => {
    Promise.all([getAuthConfig(), getMe()])
      .then(([cfg, identity]) => {
        setConfig(cfg);
        setSession(identity);
        setMe(identity);
        const ms = msUntilExpiry(identity);
        setSessionEndsAt(ms === null ? null : Date.now() + ms);
        setState(identity ? 'signed-in' : 'signed-out');
      })
      .catch((err) => {
        if (err instanceof MFAAssuranceRequired) {
          setState('mfa');
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
        setState('error');
      });
  }, []);

  // The session's own expiry: sign out AT that moment (the server refuses it from then on anyway),
  // and re-read the identity every minute so a policy change moves the moment (or ends the page at
  // once). The reload shows the sign-in screen; the Dispatcher's own session is unaffected.
  const sessionIdentity = state === 'signed-in' && me?.identity === 'session' ? me : null;
  useEffect(() => {
    if (!sessionIdentity) return;
    return watchSessionExpiry(sessionIdentity, () => getMe(), () => window.location.reload(), (m) => {
      setSession(m);
      const ms = msUntilExpiry(m);
      setSessionEndsAt(ms === null ? null : Date.now() + ms);
    });
  }, [sessionIdentity]);

  if (state === 'loading') return <div className="h-screen bg-gray-900" />;
  if (state === 'mfa') return <MFARequiredScreen />;
  if (state !== 'signed-in') return <SignInScreen config={config} error={error} />;
  return <IdentityContext.Provider value={{ me, error, sessionEndsAt }}>{children}</IdentityContext.Provider>;
}

/** The RadioOS second factor of a Microsoft/Google sign-in that carried no MFA evidence. */
export function StepUpScreen({ pending }: { pending: PendingStep }) {
  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-gray-800 border border-gray-700 rounded-lg p-8 space-y-4">
        <h1 className="text-xl font-bold text-center">Confirm with your authenticator app</h1>
        <p className="text-sm text-gray-300">
          {pending.next === 'enroll'
            ? 'Your role requires multi-factor authentication and your sign-in did not prove it. Set up an authenticator app for RadioOS to finish signing in - once.'
            : 'Your role requires multi-factor authentication and your sign-in did not prove it. Enter the code from your authenticator app.'}
        </p>
        <ManagedSignIn pending={pending} onSignedIn={() => window.location.assign('/')} />
      </div>
    </div>
  );
}

/** A valid session whose sign-in lacks the MFA the person's role requires: it carries nothing. */
export function MFARequiredScreen() {
  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-gray-800 border border-gray-700 rounded-lg p-8 space-y-4 text-center">
        <h1 className="text-xl font-bold">Multi-factor authentication required</h1>
        <p role="alert" className="text-sm text-yellow-200">
          Your role requires multi-factor authentication, and this session does not have it (for example because the requirement was
          introduced after you signed in). Sign out and sign in again - you will be asked for your authenticator app.
        </p>
        <button onClick={signOut} className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-500 font-medium">Sign out</button>
      </div>
    </div>
  );
}

export function SignInScreen({ config, error }: { config: AuthConfig | null; error: string | null }) {
  const denial = loginErrorMessage(new URLSearchParams(window.location.search).get('login_error'));
  // A Microsoft/Google sign-in without MFA evidence continues here with the RadioOS authenticator.
  const [pending] = useState<PendingStep | null>(() => takePendingStep());
  if (pending) return <StepUpScreen pending={pending} />;
  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-gray-800 border border-gray-700 rounded-lg p-8 space-y-6 text-center">
        <img src="/logo.png" alt="KAN-Consult" className="h-14 w-auto mx-auto" />
        <div>
          <h1 className="text-2xl font-bold">RadioOS Management Client</h1>
          <p className="text-gray-400 text-sm mt-1">Sign in to continue</p>
        </div>
        {denial && <p role="alert" className="text-sm text-yellow-200 bg-yellow-900/40 border border-yellow-700 rounded p-3">{denial}</p>}
        {error && <p role="alert" className="text-sm text-red-200">The server could not be reached. Please try again later.</p>}
        {config?.managed_auth_enabled && <ManagedSignIn />}
        {config?.managed_auth_enabled && (config?.entra_enabled || config?.google_enabled) && (
          <div className="flex items-center gap-3 text-xs text-gray-400" aria-hidden="true"><span className="flex-1 border-t border-gray-700" />or<span className="flex-1 border-t border-gray-700" /></div>
        )}
        {config?.entra_enabled ? (
          <button onClick={signInWithMicrosoft}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded bg-white text-gray-900 font-semibold hover:bg-gray-100">
            <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            Sign in with Microsoft
          </button>
        ) : (
          !error && !config?.managed_auth_enabled && !config?.google_enabled && <p className="text-sm text-gray-300">No sign-in method is configured for this deployment.</p>
        )}
        {config?.google_enabled && (
          <button onClick={signInWithGoogle}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded bg-white text-gray-900 font-semibold hover:bg-gray-100">
            <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.6 5.4 2.6 13.3l7.8 6C12.3 13.5 17.7 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.6 5.9c4.4-4.1 7-10.1 7-17.6z" />
              <path fill="#FBBC05" d="M10.4 28.7c-.5-1.4-.8-3-.8-4.7s.3-3.3.8-4.7l-7.8-6C.9 16.6 0 20.2 0 24s.9 7.4 2.6 10.7l7.8-6z" />
              <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2.1 1.4-4.9 2.3-8.3 2.3-6.3 0-11.7-4-13.6-9.8l-7.8 6C6.6 42.6 14.6 48 24 48z" />
            </svg>
            Continue with Google
          </button>
        )}
      </div>
    </div>
  );
}

/** Header badge: who the client acts as, sign-out, and in DEV_MODE a switcher between users. */
export function IdentityBadge() {
  const { me, sessionEndsAt } = useIdentity();
  if (!me) return null;
  const label = me.platform_admin ? 'Platform admin' : me.platform_operator ? 'Platform operator'
    : me.oem_partner ? `${me.oem_partner.role === 'oem_admin' ? 'OEM admin' : 'OEM user'} · ${me.oem_partner.name}`
    : me.grants.map((g) => `${g.role}${g.department_name ? ` · ${g.department_name}` : ''}`).join(', ') || 'No access';
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="px-2 py-1 rounded bg-gray-700 text-gray-200" title={me.grants.map((g) => `${g.role} @ ${g.tenant_name || 'platform'}${g.department_name ? ' / ' + g.department_name : ''}`).join('\n')}>
        {me.username || (me.identity === 'dev-platform' ? 'DEV' : 'user')}: {label}
      </span>
      {me.identity === 'session' && sessionEndsAt && (
        <span data-session-ends className="text-gray-400" title="Your Management Client sign-in ends at this time (set by the platform's session policy); the Dispatcher has its own sign-in">
          until {new Date(sessionEndsAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      )}
      {me.identity === 'session' && (
        <Link to="/Account" className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-200">My account</Link>
      )}
      {(me.identity === 'session' || me.identity === 'jwt') && (
        <button onClick={signOut} className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-gray-200">Sign out</button>
      )}
      {me.dev_mode && me.identity !== 'session' && (
        <select
          aria-label="DEV: act as user"
          className="bg-yellow-900 text-yellow-100 border border-yellow-700 rounded px-2 py-1"
          value={getDevUser()}
          onChange={(e) => {
            setDevUser(e.target.value);
            window.location.reload();
          }}
        >
          <option value="">DEV: platform admin</option>
          {(me.dev_users || []).map((u) => (
            <option key={u.id} value={u.id}>DEV as {u.username}{u.tenant ? ` (${u.tenant})` : ''}</option>
          ))}
        </select>
      )}
    </div>
  );
}
